import React, { useState, useEffect } from 'react';

interface SkewedCardProps {
  children: React.ReactNode;
  className?: string;
  borderWidth?: number;
  borderColor?: string;
  backgroundColor?: string;
  variance?: number;
  padding?: string;
  onClick?: () => void;
}

interface Point {
  x: number;
  y: number;
}

/**
 * A card/container component with randomly skewed quadrilateral borders using SVG.
 * Similar to SkewedBox but more flexible for various use cases.
 */
const SkewedCard: React.FC<SkewedCardProps> = ({
  children,
  className = '',
  borderWidth = 2,
  borderColor = 'black',
  backgroundColor = 'white',
  variance = 0.06,
  padding = 'p-6',
  onClick
}) => {
  const [points, setPoints] = useState<Point[]>([]);

  useEffect(() => {
    // Generate random offset within variance range
    const getRandomOffset = () => {
      return (Math.random() - 0.5) * variance * 100;
    };

    // Generate four corners with slight random variations
    const corners = [
      { x: 0 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-left
      { x: 100 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-right
      { x: 100 + getRandomOffset(), y: 100 + getRandomOffset() }, // bottom-right
      { x: 0 + getRandomOffset(), y: 100 + getRandomOffset() } // bottom-left
    ];

    setPoints(corners);
  }, [variance]);

  // Convert percentage points to SVG polygon points string
  const svgPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  const containerClasses = `relative ${className} ${onClick ? 'cursor-pointer' : ''}`;

  return (
    <div className={containerClasses} onClick={onClick}>
      {/* SVG border overlay */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <polygon
          points={svgPoints}
          fill={backgroundColor}
          stroke={borderColor}
          strokeWidth={borderWidth / 10}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Content layer */}
      <div className={`relative ${padding}`}>
        {children}
      </div>
    </div>
  );
};

export default SkewedCard;
